import { buildNameMapFromTemplate, filterKnownFindings } from '@/tools/models/compatibilityDiff.utilities';
import { computePreviouslySelectedOutputCanonicalIds } from '@/tools/models/templateOutputStatus.utilities';
import {
  GetInstanceQueryKey,
  useApiDiffTemplatesQuery,
  useApiGetInstanceQuery,
  useApiGetTemplateVersionQuery,
} from '@seeqdev/api/endpoint/TemplatesApi';
import type { QueryClient } from '@tanstack/react-query';
import type { TemplateDerivedFeature } from '@/tools/models/template.types';

/**
 * Removes the cached instance so the next reapply loads a fresh snapshot.
 *
 * Uses cache removal rather than invalidation to avoid seeding from stale data.
 */
export function evictInstanceQuery(queryClient: QueryClient, instanceId: string) {
  return queryClient.removeQueries({ queryKey: GetInstanceQueryKey({ instanceId }) });
}

/**
 * Fetches the compatibility diff between an instance's currently-applied template version
 * and a target version selected for an upgrade. The base side comes from the instance
 * itself (its `versionId` is the version it was last applied with); the candidate side
 * is whatever the consumer picked in the version-picker modal.
 *
 * Also derives reapplication output selection state: previously selected outputs and outputs
 * newly introduced in the target version.
 *
 * Returns a no-op (`hasDifferentVersion: false`, empty findings) when:
 *   - `instanceId` is undefined (the new-application flow, no upgrade in progress), or
 *   - the target equals the instance's current version (nothing to upgrade to).
 */
export function useInstanceCompatibilityCheck(
  instanceId: string | undefined,
  targetVersionId: string | undefined,
  currentDerivedFeatures: TemplateDerivedFeature[] = [],
) {
  const instanceQuery = useApiGetInstanceQuery({ instanceId: instanceId! }, { enabled: !!instanceId });
  const baseVersionId = instanceQuery.data?.templateId;
  const modelContainerId = instanceQuery.data?.templateContainerId;
  const hasDifferentVersion = !!baseVersionId && !!targetVersionId && baseVersionId !== targetVersionId;

  const diffQuery = useApiDiffTemplatesQuery(
    { baseTemplateId: baseVersionId!, candidateTemplateId: targetVersionId! },
    { enabled: hasDifferentVersion },
  );

  const baseVersionQuery = useApiGetTemplateVersionQuery(
    { templateId: modelContainerId!, versionId: baseVersionId! },
    { enabled: hasDifferentVersion && !!modelContainerId },
  );

  const candidateVersionQuery = useApiGetTemplateVersionQuery(
    { templateId: modelContainerId!, versionId: targetVersionId! },
    { enabled: hasDifferentVersion && !!modelContainerId },
  );

  const baseFeatureNameMap = buildNameMapFromTemplate(baseVersionQuery.data);
  const candidateFeatureNameMap = buildNameMapFromTemplate(candidateVersionQuery.data);
  /**
   * Union of base + candidate, keyed by canonicalId. Covers every feature the server can
   * reference from a finding (added features come from candidate, removed from base,
   * retained from either). Candidate wins on conflict so renamed features show their
   * current name.
   */
  const featureNameMap = new Map([...baseFeatureNameMap, ...candidateFeatureNameMap]);

  const findings = filterKnownFindings(diffQuery.data?.findings ?? []);

  // Same-version reapply: currentDerivedFeatures IS the base version, no separate fetch needed.
  const baseDerivedFeatures = hasDifferentVersion
    ? (baseVersionQuery.data?.derivedFeatures ?? [])
    : currentDerivedFeatures;
  const previouslySelectedOutputCanonicalIds = computePreviouslySelectedOutputCanonicalIds(
    instanceQuery.data?.outputs ?? [],
    baseDerivedFeatures,
  );
  const newOutputCanonicalIds = new Set(
    findings
      .filter((f) => f.reasonCode === 'OUTPUT_FEATURE_ADDED')
      .map((f) => f.featureCanonicalId?.toLowerCase())
      .filter((id): id is string => !!id),
  );

  return {
    findings,
    featureNameMap,
    hasDifferentVersion,
    previouslySelectedOutputCanonicalIds,
    newOutputCanonicalIds,
    isLoading: instanceQuery.isLoading || (hasDifferentVersion && diffQuery.isLoading),
  };
}
